import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import tradingService from '../services/tradingService';
import ModalShell from './ModalShell';
import AddTradeModal from './AddTradeModal';

const isCanceledRequest = (err) => {
  if (!err) return false;
  return (
    err?.name === 'AbortError' || // fetch / some libs
    err?.name === 'CanceledError' || // axios v1
    err?.code === 'ERR_CANCELED' || // axios v1
    String(err?.message || '').toLowerCase() === 'canceled'
  );
};

// AccountTradesSection: shows paginated trades for an account, plus actions (refresh/add) for the trades list.
export default function AccountTradesSection({ number }) {
  const navigate = useNavigate();

  // trades state (paginated)
  const [tradesLoading, setTradesLoading] = useState(false);
  const [tradesRefreshing, setTradesRefreshing] = useState(false);
  const [tradesHasLoaded, setTradesHasLoaded] = useState(false);

  const [tradesError, setTradesError] = useState('');
  const [trades, setTrades] = useState([]);
  const [tradesCount, setTradesCount] = useState(0);
  const [tradesLimit, setTradesLimit] = useState(10);
  const [tradesOffset, setTradesOffset] = useState(0);

  const [hoveredTradeOrderId, setHoveredTradeOrderId] = useState(null);

  // add-trade modal state (UI)
  const [addTradeOpen, setAddTradeOpen] = useState(false);
  const [addTradeError, setAddTradeError] = useState('');
  const [reloadNonce, setReloadNonce] = useState(0);

  const tradesPage = useMemo(
    () => Math.floor(tradesOffset / Math.max(1, tradesLimit)) + 1,
    [tradesOffset, tradesLimit]
  );
  const tradesTotalPages = useMemo(
    () => Math.max(1, Math.ceil((tradesCount || 0) / Math.max(1, tradesLimit))),
    [tradesCount, tradesLimit]
  );
  const tradesCanPrev = tradesOffset > 0;
  const tradesCanNext = tradesOffset + tradesLimit < (tradesCount || 0);

  useEffect(() => {
    if (!number) return;

    const id = window.setInterval(() => {
      setReloadNonce((n) => n + 1);
    }, 10_000);

    return () => window.clearInterval(id);
  }, [number, tradesLimit, tradesOffset]);

  useEffect(() => {
    if (!number) return;

    const ac = new AbortController();
    let alive = true;

    (async () => {
      const isBackground = tradesHasLoaded; // if we've shown data once, refresh in background

      try {
        if (isBackground) setTradesRefreshing(true);
        else setTradesLoading(true);

        // don't wipe the list while fetching; just clear the error
        setTradesError('');

        const res = await tradingService.accounts.trades.listForAccount({
          number,
          limit: tradesLimit,
          offset: tradesOffset,
          signal: ac.signal,
        });

        if (!alive) return;

        const payload = res?.data ?? {};
        const list = payload?.trades ?? [];
        setTrades(Array.isArray(list) ? list : []);
        setTradesCount(Number(payload?.count ?? 0));
        setTradesHasLoaded(true);
      } catch (err) {
        if (!alive || isCanceledRequest(err) || ac.signal.aborted) return;

        // don't clear the table on refresh errors; keep last good data
        setTradesError(err?.message || 'Failed to load trades');
      } finally {
        if (!alive) return;
        setTradesLoading(false);
        setTradesRefreshing(false);
      }
    })();

    return () => {
      alive = false;
      ac.abort();
    };
  }, [number, tradesLimit, tradesOffset, reloadNonce, tradesHasLoaded]);


  const formatDateTime = (value) => {
    if (!value) return '—';

    // Accept:
    // - ISO strings (with/without timezone)
    // - "YYYY-MM-DD HH:mm:ss" (common from backends) -> treat as UTC and convert to ISO
    let s = String(value).trim();

    // If it's "2025-01-15 18:36:12" => make it ISO-ish.
    // We append "Z" to avoid local-time ambiguity and get a real ISO timestamp.
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(s)) {
      s = s.replace(' ', 'T') + 'Z';
    }

    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return String(value);

    // ISO 8601 without milliseconds: 2025-01-15T18:36:12Z
    return d.toISOString().replace(/\.\d{3}Z$/, 'Z');
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined || value === '') return '—';

    // Normalize scientific notation strings like "0e-8" / "1.23E-7"
    if (typeof value === 'string') {
      const s = value.trim();
      const sci = /^-?\d+(\.\d+)?e[+-]?\d+$/i;
      if (sci.test(s)) {
        const n = Number(s);
        if (!Number.isFinite(n)) return s;

        const fixed = n.toFixed(8);
        return fixed.replace(/\.?0+$/, '');
      }

      return s;
    }

    if (typeof value === 'number') {
      if (!Number.isFinite(value)) return String(value);
      const fixed = value.toFixed(8);
      return fixed.replace(/\.?0+$/, '');
    }

    return String(value);
  };

  const getTradeStatusClassName = (trade_status_id) => {
    const raw = trade_status_id;
    if (!trade_status_id) return 'trade-status trade-status--unknown';
    if (raw === 1) return 'trade-status trade-status--created';
    if (raw === 2) return 'trade-status trade-status--failed';
    if (raw === 3) return 'trade-status trade-status--executed';
    return 'trade-status trade-status--unknown';
  };

  const goToTradeDetails = (tradeOrderId) => {
    if (!tradeOrderId) return;
    navigate(`/my/accounts/${encodeURIComponent(number)}/trades/${encodeURIComponent(tradeOrderId)}`);
  };

  return (
    <section className="account-details__section">
      <div className="account-details__section-header">
        <div>
          <div className="account-details__section-title">Trades</div>
          <div className="account-details__section-subtitle">
            {tradesCount ? (
              <>
                Showing {tradesOffset + 1}–{Math.min(tradesOffset + tradesLimit, tradesCount)} of {tradesCount}
              </>
            ) : (
              'All trades for this account.'
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 12, opacity: 0.8 }}>Page size</span>
            <select
              value={String(tradesLimit)}
              onChange={(e) => {
                const next = Math.max(1, Number(e.target.value) || 10);
                setTradesOffset(0);
                setTradesLimit(next);
              }}
              className="account-details__control"
              style={{ width: 110 }}
              disabled={tradesLoading || tradesRefreshing}
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
            </select>
          </label>

          <button
            type="button"
            className="account-details__btn"
            onClick={() => setTradesOffset((o) => Math.max(0, o - tradesLimit))}
            disabled={!tradesCanPrev || tradesLoading || tradesRefreshing}
          >
            Prev
          </button>
          <button
            type="button"
            className="account-details__btn"
            onClick={() => setTradesOffset((o) => o + tradesLimit)}
            disabled={!tradesCanNext || tradesLoading || tradesRefreshing}
          >
            Next
          </button>

          <div style={{ fontSize: 12, opacity: 0.8 }}>
            Page {tradesPage} / {tradesTotalPages}
          </div>

          <button
            type="button"
            className="account-details__btn"
            onClick={() => setReloadNonce((n) => n + 1)}
            disabled={tradesLoading || tradesRefreshing}
            title="Reload current page"
          >
            Refresh
          </button>

          {tradesRefreshing ? (
            <div style={{ fontSize: 12, opacity: 0.75, whiteSpace: 'nowrap' }}>Refreshing…</div>
          ) : null}

          <button
            type="button"
            onClick={() => {
              setAddTradeError('');
              setAddTradeOpen(true);
            }}
            disabled={!number || tradesLoading || tradesRefreshing}
            title="Add trade"
            aria-label="Add trade"
            style={{
              width: 34,
              height: 34,
              borderRadius: 999,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
              lineHeight: 1,
              border: '1px solid rgba(148, 163, 184, 0.18)',
              background: 'rgba(13, 23, 40, 0.88)',
              color: '#edf5ff',
              cursor: !number || tradesLoading || tradesRefreshing ? 'not-allowed' : 'pointer',
              boxShadow: '0 10px 24px rgba(2, 8, 23, 0.28)',
            }}
          >
            +
          </button>
        </div>
      </div>

      {tradesError ? <div className="account-details__error">{tradesError}</div> : null}
      {addTradeError ? <div className="account-details__error">{addTradeError}</div> : null}

      {tradesLoading && !tradesHasLoaded ? <div>Loading…</div> : null}

      <ModalShell
        title="Add trade"
        open={addTradeOpen}
        onClose={() => setAddTradeOpen(false)}
        footer={null}
      >
        <AddTradeModal
          // number: which account this new trade will be created for
          number={number}
          // onCancel: closes the modal without creating anything
          onCancel={() => setAddTradeOpen(false)}
          // onCreated: called after successful create -> close modal and reload current trades page
          onCreated={() => {
            setAddTradeOpen(false);
            setReloadNonce((n) => n + 1);
          }}
          // onError: bubbles API/form errors to section-level message area
          onError={(msg) => setAddTradeError(msg || 'Failed to add trade')}
        />
      </ModalShell>

      {!tradesLoading && !tradesError ? (
        trades.length ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left' }}>
                  <th style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>Created</th>
                  <th style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>Symbol</th>
                  <th style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>Direction</th>
                  <th style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>Status</th>
                  <th style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>Executed</th>
                  <th style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>Quote qty</th>
                  <th style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>Comment</th>
                </tr>
              </thead>
              <tbody>
                {trades.map((t) => {
                  const tradeOrderId = t?.trade_order_id;
                  const canOpen = Boolean(number && tradeOrderId);
                  const isHovered = hoveredTradeOrderId === tradeOrderId;

                  return (
                    <tr
                      key={tradeOrderId}
                      onClick={() => (canOpen ? goToTradeDetails(tradeOrderId) : null)}
                      onMouseEnter={() => (canOpen ? setHoveredTradeOrderId(tradeOrderId) : null)}
                      onMouseLeave={() => setHoveredTradeOrderId(null)}
                      onKeyDown={(e) => {
                        if (!canOpen) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          goToTradeDetails(tradeOrderId);
                        }
                      }}
                      tabIndex={canOpen ? 0 : -1}
                      role={canOpen ? 'button' : undefined}
                      aria-label={canOpen ? `Open trade ${tradeOrderId} details` : undefined}
                      style={{
                        background: canOpen && isHovered ? 'rgba(56, 189, 248, 0.06)' : undefined,
                        cursor: canOpen ? 'pointer' : 'default',
                        transition: 'background 120ms ease',
                      }}
                      title={canOpen ? 'Click to view trade details' : undefined}
                    >
                      <td style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>{formatDateTime(t.date_created)}</td>
                      <td style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>
                        <code>{t?.symbol?.name ?? '—'}</code>
                      </td>
                      <td style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>{t?.direction?.name ?? '—'}</td>
                      <td style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>
                        <span className={getTradeStatusClassName(t?.trade_status?.trade_status_id)}>
                          {t?.trade_status?.label ?? '—'}
                        </span>
                      </td>
                      <td style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>
                        {formatNumber(t.executed_quantity)} @ {formatNumber(t.executed_price)}
                      </td>
                      <td style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>{formatNumber(t.quote_quantity)}</td>
                      <td style={{ padding: '10px 8px' }}>{t.comment ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div>No trades found.</div>
        )
      ) : null}
    </section>
  );
}
